import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormInput } from "components/BaseForm";
import BaseForm from "components/BaseForm/BaseForm";
import { swoogoRegistrantKeys } from "routes/swoogo/queryKeys";
import SwoogoService from "services/Swoogo";

const schema = z.object({
  first_name: z.string().trim().min(1, "First name is required"),
  last_name: z.string().trim(),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Enter a valid email"),
});

const FIELDS = ["first_name", "last_name", "email"];

const RegistrantEditForm = ({ registrant }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const overviewPath = `/swoogo/registrants/${registrant.id}/overview`;
  const defaultValues = {
    first_name: registrant.first_name ?? "",
    last_name: registrant.last_name ?? "",
    email: registrant.email ?? "",
  };

  const updateMutation = useMutation({
    mutationFn: changes =>
      SwoogoService.updateRegistrant(registrant.id, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: swoogoRegistrantKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: swoogoRegistrantKeys.detail(registrant.id),
      });
      toast.success("Registrant updated");
      navigate(overviewPath);
    },
  });

  const handleSubmit = values => {
    const changes = Object.fromEntries(
      FIELDS.filter(field => values[field] !== defaultValues[field]).map(
        field => [field, values[field]]
      )
    );
    if (Object.keys(changes).length === 0) {
      navigate(overviewPath);
      return undefined;
    }
    return updateMutation.mutateAsync(changes);
  };

  return (
    <div className="tw:w-full tw:max-w-7xl">
      <BaseForm
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={() => navigate(overviewPath)}
        submitButtonText="Save"
      >
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
              <FormInput name="first_name" label="First Name" required />
              <FormInput name="last_name" label="Last Name" />
              <FormInput name="email" label="Email" type="email" required />
            </div>
          </CardContent>
        </Card>
      </BaseForm>
    </div>
  );
};

RegistrantEditForm.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
};

export default RegistrantEditForm;
